import api from '../axios';
import { handleApiError } from '../errorHandler';
import {
  INotificationResponse,
  ICreateNotificationRequest,
  IUpdateNotificationRequest,
  ICreateNotificationReadRequest,
  IUpdateNotificationReadRequest,
  IQueryNotificationParams,
  INotificationsPagedResponse,
  INotificationCountResponse,
  IMarkAllReadResponse,
  INotificationReadResponse,
  ITeamAcknowledgeRequest,
  ITeamAcknowledgeResponse,
  NotificationType,
} from './types';

/**
 * Serviços específicos para Notificações
 */

const BASE_URL = '/notifications';

/**
 * Criar uma nova notificação
 */
export const createNotification = async (
  data: ICreateNotificationRequest
): Promise<INotificationResponse> => {
  try {
    const response = await api.post<INotificationResponse>(BASE_URL, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar notificação:', error);
    throw handleApiError(error);
  }
};

/**
 * Listar notificações do usuário logado com filtros e paginação
 */
export const getNotifications = async (
  params?: IQueryNotificationParams
): Promise<INotificationsPagedResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.read !== undefined) {
      // true/false explícitos — alguns parsers tratam só "false" como string truthy
      queryParams.append('read', params.read ? 'true' : 'false');
    }
    if (params?.type) {
      queryParams.append('type', params.type);
    }
    if (params?.orderBy) {
      queryParams.append('orderBy', params.orderBy);
    }
    if (params?.order) {
      queryParams.append('order', params.order);
    }
    if (params?.scope !== undefined && params.scope !== null) {
      queryParams.append('scope', String(params.scope));
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;
    const response = await api.get<INotificationsPagedResponse>(url);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao buscar notificações:', error?.message || error);
    throw handleApiError(error);
  }
};

/**
 * Contar notificações não lidas
 */
export const getNotificationCount = async (type?: NotificationType): Promise<number> => {
  try {
    const params = new URLSearchParams();
    if (type) {
      params.append('type', type);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${BASE_URL}/count-unread?${queryString}`
      : `${BASE_URL}/count-unread`;

    const response = await api.get<INotificationCountResponse>(url);
    return response.data.count;
  } catch (error) {
    console.error('Erro ao contar notificações não lidas:', error);
    throw handleApiError(error);
  }
};

/**
 * Obter notificação por ID
 */
export const getNotification = async (id: string): Promise<INotificationResponse> => {
  try {
    const response = await api.get<INotificationResponse>(`${BASE_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar notificação:', error);
    throw handleApiError(error);
  }
};

/**
 * Deletar notificação (soft delete)
 */
export const deleteNotification = async (id: string): Promise<void> => {
  try {
    await api.delete(`${BASE_URL}/${id}`);
  } catch (error) {
    console.error('Erro ao deletar notificação:', error);
    throw handleApiError(error);
  }
};

/**
 * Marcar notificação como lida
 * NOTA: O backend preenche readAt automaticamente. Não é necessário enviar 'read: true'.
 */
export const markNotificationAsRead = async (
  id: string,
  data?: ICreateNotificationReadRequest
): Promise<INotificationReadResponse> => {
  try {
    const response = await api.post<INotificationReadResponse>(
      `${BASE_URL}/${id}/read`,
      data || {}
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao marcar notificação como lida:', error);
    throw handleApiError(error);
  }
};

/**
 * Marcar todas as notificações não lidas como lidas
 */
export const markAllNotificationsAsRead = async (
  type?: NotificationType
): Promise<IMarkAllReadResponse> => {
  try {
    const params = new URLSearchParams();
    if (type) {
      params.append('type', type);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${BASE_URL}/mark-all-read?${queryString}`
      : `${BASE_URL}/mark-all-read`;

    const response = await api.post<IMarkAllReadResponse>(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao marcar todas as notificações como lidas:', error);
    throw handleApiError(error);
  }
};

/**
 * Atualizar leitura/feedback de uma notificação
 */
export const updateNotificationRead = async (
  id: string,
  data: IUpdateNotificationReadRequest
): Promise<INotificationReadResponse> => {
  try {
    const response = await api.patch<INotificationReadResponse>(`${BASE_URL}/${id}/read`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar leitura da notificação:', error);
    throw handleApiError(error);
  }
};

/**
 * Atualizar uma notificação
 */
export const updateNotification = async (
  id: string,
  data: IUpdateNotificationRequest
): Promise<INotificationResponse> => {
  try {
    const response = await api.patch<INotificationResponse>(`${BASE_URL}/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar notificação:', error);
    throw handleApiError(error);
  }
};

/**
 * Team acknowledge compartilhado — primeiro admin a abrir a revisão.
 * 201 na primeira vez; 200 se idempotente.
 */
export const acknowledgeTeamNotification = async (
  id: string,
  data: ITeamAcknowledgeRequest
): Promise<ITeamAcknowledgeResponse> => {
  try {
    const response = await api.post<ITeamAcknowledgeResponse>(
      `${BASE_URL}/${id}/acknowledge`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao confirmar abertura da notificação (team acknowledge):', error);
    throw handleApiError(error);
  }
};
